import React from 'react'
import { useQuery } from '@apollo/client'
import { Button, Callout } from '@superhi/design'

import CourseCollections from '../../../../components/sections/CourseCollections'

import { Props } from './data'
import ROUTE from './route'
import Hero from './components/Hero'
import { COURSE_ACCESS } from './queries'
import Intro, { BETA_COURSES_ID } from './components/Intro'
import Syllabus, { ID as SYLLABUS_ID } from './components/Syllabus'
import Instructors from './components/Instructors'

import { buildPath } from 'src/routing'
import { SUPERLIBRARY_URL } from 'src/config'
import { API } from 'src/api'
import Aside from 'src/components/Aside'
import Main from 'src/components/Main'
import Page from 'src/components/Page'
// import Editor from "src/components/sections/Editor";
import HelpCenterArticles from 'src/components/sections/HelpCenterArticles'
import SuperLibraryGuides from 'src/components/sections/SuperLibraryGuides'
import SuperLibraryPosts from 'src/components/sections/SuperLibraryPosts'
import { PageConfig } from 'src/routing/types'
import SEO from 'src/components/SEO'
import TopCourses from 'src/components/sections/TopCourses'

// import Overview, { ID as OVERVIEW_ID } from "./components/Overview";

export const FAQS_ID = 'course-faqs'

// const SUB_NAV_ITEMS: React.ComponentProps<typeof SubNav>["items"] = [
//   {
//     id: INTRO_ID,
//     display: "Overview",
//   },
//   {
//     id: WHAT_YOULL_LEARN_ID,
//     display: "What you'll learn",
//   },
//   {
//     id: SYLLABUS_ID,
//     display: "Syllabus",
//   },
//   {
//     id: FAQS_ID,
//     display: "FAQs",
//   },
// ];

const Course: PageConfig<Props> = ({
  id,
  title,
  course,
  subjects,
  slug,
  releaseDate,
  isBeta,
  description,
  requirements,
  targetAudience,
  overview,
  mailchimpTag,
  syllabusUrl,
  contents,
  projects,
  instructors,
  variants,
  lowestPrice,
  shortDescription,
  length,
  image,
  difficultyLevel,
  faqs,
  topGuides,
  topPosts,
  otherCourses,
}) => {
  const { data: sessionData } = useQuery<API.Course_CourseAccess>(COURSE_ACCESS, {
    ssr: false,
  })

  const reversedInstructors = [...(instructors || [])].reverse()

  const hasAccessToCourse =
    (sessionData?.session &&
      ([API.UserType.MEMBER, API.UserType.TEAM_MEMBER].includes(sessionData.session.type) ||
        sessionData.session.isAdmin)) ||
    sessionData?.session?.courses?.nodes.some((item) => item.course.id === course.id) ||
    false

  const isFree = lowestPrice.value === 0
  const hasFaqs = !!faqs && faqs.length > 0

  return (
    <Page id={slug}>
      <SEO title={title} description={shortDescription} imageUrl={image.url} imageFm="png8" />

      <Main>
        <Hero
          id={id}
          slug={slug}
          title={title}
          duration={length}
          description={shortDescription}
          backgroundImage={image}
          difficultyLevel={difficultyLevel}
          variantId={variants.nodes[0].id}
          price={{
            value: lowestPrice.value,
            currencyCode: lowestPrice.currencyCode,
          }}
          releaseDate={releaseDate}
          hasAccessToCourse={hasAccessToCourse}
          dashboardCourseSlug={course.location}
          isBeta={isBeta}
          betaCoursesId={BETA_COURSES_ID}
          hasFaqs={hasFaqs}
          faqsId={FAQS_ID}
          syllabusId={SYLLABUS_ID}
        />

        {/* <SubNav level="2" currentId="" items={SUB_NAV_ITEMS} /> */}

        <Intro
          description={description}
          requirements={requirements}
          targetAudience={targetAudience}
          isBeta={isBeta}
          overview={overview}
          contents={contents}
          noBottomPadding={isFree}
        />

        {!isFree && (
          <Callout
            title="Want to try our teaching before signing up? Join our free 'Plan, Design + Code Your First Website' course!"
            backgroundColor="pink"
          >
            <Button
              level={Callout.ButtonLevel}
              href={buildPath('courses-view', {
                params: { slug: 'plan-design-and-code-your-first-website' },
              })}
            >
              View course
            </Button>
          </Callout>
        )}

        <Syllabus
          courseMailchimpTag={mailchimpTag}
          syllabusUrl={syllabusUrl}
          emailModalImageUrl={image.url}
          chapters={(projects || []).map((c, i) => ({ ...c, position: i + 1 }))}
        />

        <Aside>
          <Callout
            backgroundColor="blue"
            title="Not ready to sign up yet? Try one of our free tutorials to get a taster of how we teach!"
          >
            <Button level={Callout.ButtonLevel} href={`${SUPERLIBRARY_URL}/tutorials`} isExternal>
              View tutorials
            </Button>
          </Callout>
        </Aside>

        <Aside>
          <CourseCollections productCourseSlug={slug} imagesLazy />
        </Aside>


        {reversedInstructors && reversedInstructors.length > 0 && (
          <Instructors instructors={reversedInstructors} />
        )}

        {/* {subjects.includes(API.ProductCourseSubject.CODE) && (
        <Editor title="The SuperHi Editor" noBottomPadding />
      )} */}

        {otherCourses && (
          <TopCourses
            {...otherCourses}
            title="You might also like..."
            subtitle={undefined}
            imagesLazy
          />
        )}

        <span>
          {difficultyLevel === 'BEGINNER'
            ? topGuides && (
                <SuperLibraryGuides
                  {...topGuides}
                  title="Free resources to get you started"
                  noTopPadding
                  imagesLazy
                  headingLevel={2}
                />
              )
            : topPosts && (
                <SuperLibraryPosts
                  {...topPosts}
                  title="Still looking around? "
                  subtitle="View our growing collection of resources, articles and tutorials. We're adding new things to read, learn and watch every week!"
                  subtitleMaxWidth={30}
                  noTopPadding
                  imagesLazy
                  headingLevel={2}
                />
              )}
        </span>

        {faqs && faqs.length > 0 && (
          <HelpCenterArticles
            __typename="HelpCenterArticleList"
            nodes={faqs}
            id={FAQS_ID}
            title="FAQs"
          />
        )}
      </Main>
    </Page>
  )
}

Course.route = ROUTE

export { getStaticProps, getStaticPaths } from './data'
export default Course
